import { Button } from "@/components/generic/Button";
import { PageContent } from "@/components/layout/MainWindow";
import { PageHeader } from "@/components/layout/PageHeader";
import {
  useAvailableIntegrationsQuery,
  useIntegrationsQuery,
} from "@/queries/integrations";
import { Integration } from "@/types/integrations";
import { getIntegrationConfig } from "@/utils/integration-helpers";
import { createFileRoute } from "@tanstack/react-router";
import { useMemo } from "react";
import { z } from "zod";

export const Route = createFileRoute(
  "/_dashboard/settings/_admin/integrations/",
)({
  component: RouteComponent,
  pendingMinMs: 500,
  pendingMs: 500,
  pendingComponent: () => (
    <div className="flex h-full flex-col">
      <PageHeader title="Integrationer" />

      <PageContent className="pt-7">
        <div className="grid grid-cols-1 gap-6 pb-16 lg:grid-cols-2 2xl:grid-cols-3">
          <div className="col-span-1 lg:col-span-2 2xl:col-span-3">
            <div className="flex h-full items-center justify-center">
              <span>Indlæser integrationer...</span>
            </div>
          </div>
        </div>
      </PageContent>
    </div>
  ),
  validateSearch: (search: { ignoreRedirect?: string }) =>
    z
      .object({
        ignoreRedirect: z.coerce.boolean().optional(),
      })
      .parse(search),
});

const categoryOrder: Integration["category"][] = [
  "CRM",
  "Dialer",
  "Storage",
  "Other",
];

const categoryLabels = {
  CRM: "CRM",
  Dialer: "Dialer",
  Storage: "Noter",
  Other: "Andre",
};

const categoryDescriptions = {
  CRM: null,
  Dialer: null,
  Storage:
    "Tilføj en note-integration for at aktivere muligheden for at skrive noter på dine leads i Lasso Sales. Noterne gemmes direkte i den valgte integration, men vises på lead kortet i Lasso Sales.",
  Other: null,
};

// ? Exclude CustomAPI and LeadDesk as it is not implemented in the frontend for setup
const implementedIntegrations = [
  ...getIntegrationConfig().allowedIntegrations.filter(
    (c) => !["CustomAPI", "LeadDesk"].includes(c),
  ),
];

function RouteComponent() {
  const { data: integrations = [] } = useIntegrationsQuery();
  const { data: availableIntegrationsQueryData = [] } =
    useAvailableIntegrationsQuery();

  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  const availableIntegrations = useMemo(() => {
    return availableIntegrationsQueryData.filter((i) =>
      implementedIntegrations.includes(i.uniqueName),
    );
  }, [availableIntegrationsQueryData]);

  if (integrations.length === 1 && search.ignoreRedirect !== true) {
    navigate({
      to: "/settings/integrations/$integration/settings",
      params: {
        integration: integrations[0].provider.toLowerCase(),
      },
      replace: true,
    });
  }
  const hasSetupIntegration = (integrationName: string) => {
    return integrations?.some(
      (i) => i.provider.toLowerCase() === integrationName.toLowerCase(),
    );
  };

  const integrationsByCategory = useMemo(() => {
    const grouped = new Map<Integration["category"], Integration[]>();

    for (const integration of availableIntegrations) {
      const category = integration.category || "Other";
      const existing = grouped.get(category) || [];
      grouped.set(category, [...existing, integration]);
    }

    return categoryOrder
      .filter((cat) => grouped.has(cat))
      .map((cat) => ({
        category: cat,
        integrations: grouped.get(cat) ? grouped.get(cat)! : [],
      }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableIntegrations]);

  return (
    <div className="flex h-full flex-col">
      <PageHeader title="Integrationer" />

      <PageContent className="pt-7">
        <div className="flex flex-col gap-12 px-14 pb-16">
          {integrationsByCategory.map(
            ({ category, integrations: categoryIntegrations }) => (
              <div key={category}>
                <div className="mb-6 flex flex-col items-center justify-center gap-y-4">
                  <h2 className="text-md text-center font-medium">
                    {categoryLabels[category]}
                  </h2>
                  {categoryDescriptions[category] && (
                    <p className="text-theme-on-background-variant mx-auto max-w-2xl text-center text-xs">
                      {categoryDescriptions[category]}
                    </p>
                  )}
                </div>
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 2xl:grid-cols-3">
                  {categoryIntegrations.map((integration) => (
                    <div
                      key={integration.uniqueName}
                      className="border-theme-outline-variant rounded-sm border"
                    >
                      <div className="flex flex-col items-start gap-4 px-6 py-8 lg:flex-row lg:items-center lg:gap-2">
                        <div
                          className="*:size-10 lg:*:size-11"
                          dangerouslySetInnerHTML={{
                            __html: integration.iconSVG,
                          }}
                        />
                        <div className="mr-auto flex grow flex-col gap-1 overflow-hidden lg:ml-2">
                          <h3 className="truncate text-base font-semibold whitespace-nowrap">
                            {integration.name}
                          </h3>
                        </div>
                        <div className="flex gap-x-2">
                          <Button
                            intent="secondary"
                            className="w-auto min-w-0 shrink grow-0"
                            link={
                              hasSetupIntegration(integration.uniqueName)
                                ? {
                                    to: "/settings/integrations/$integration/settings",
                                    params: {
                                      integration:
                                        integration.uniqueName.toLowerCase(),
                                    },
                                  }
                                : {
                                    to: "/settings/integrations/$integration/connect",
                                    params: {
                                      integration:
                                        integration.uniqueName.toLowerCase(),
                                    },
                                    search: {
                                      mode: "setup",
                                    },
                                    mask: {
                                      to: "/settings/integrations/$integration/connect",
                                      params: {
                                        integration:
                                          integration.uniqueName.toLowerCase(),
                                      },
                                    },
                                  }
                            }
                          >
                            {hasSetupIntegration(integration.uniqueName)
                              ? "Tilpas"
                              : "Aktivér"}
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ),
          )}
          {(!availableIntegrations || availableIntegrations.length === 0) && (
            <div className="col-span-1 lg:col-span-2 2xl:col-span-3">
              <div className="flex h-full items-center justify-center">
                <span>Ingen integrationer fundet</span>
              </div>
            </div>
          )}
        </div>
      </PageContent>
    </div>
  );
}
